import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { useChatCore } from '@fly/shared-hooks';
import {
  clearQueuedMessages,
  dequeueQueuedMessage,
  enqueueQueuedMessage,
  removeQueuedMessageAtIndex,
  selectChat,
  selectQueuedMessages,
  selectSuggestions,
  useAppDispatch,
  useListThreadsInfiniteQuery,
} from '@fly/store';

import { useThreadActions } from './useThreadActions';
import { useThreadInit } from './useThreadInit';
import { useThreadResolver } from './useThreadResolver';

export const useChatStream = () => {
  const dispatch = useAppDispatch();
  const { currentThread, messageIds, isThreadLoading, isThreadEmpty } =
    useThreadResolver();

  const { sendMessage, streamRef } = useChatCore(currentThread);

  // Pre-fetch threads to improve the initial loading experience
  useListThreadsInfiniteQuery();

  const actions = useThreadActions({ streamRef });

  const chat = useSelector(selectChat);
  const suggestions = useSelector(selectSuggestions);
  const queuedMessages = useSelector(selectQueuedMessages);

  const onEnqueueMessage = useCallback(
    (message: string) => {
      dispatch(enqueueQueuedMessage(message));
    },
    [dispatch]
  );

  const onDequeueMessage = useCallback(() => {
    dispatch(dequeueQueuedMessage());
  }, [dispatch]);

  const onRemoveQueuedMessage = useCallback(
    (index: number) => {
      dispatch(removeQueuedMessageAtIndex(index));
    },
    [dispatch]
  );

  const onClearQueuedMessages = useCallback(() => {
    dispatch(clearQueuedMessages());
  }, [dispatch]);

  useThreadInit({
    currentThread,
    isThreadLoading,
  });

  useEffect(() => {
    const ref = streamRef;
    return () => {
      ref.current?.close();
    };
  }, [streamRef]);

  return {
    threadTitle: currentThread?.name,
    currentThread,
    messageIds,
    suggestions,
    queuedMessages,
    isThreadLoading,
    isThreadEmpty,

    sendMessage,
    enqueueMessage: onEnqueueMessage,
    dequeueMessage: onDequeueMessage,
    removeQueuedMessage: onRemoveQueuedMessage,
    clearQueuedMessages: onClearQueuedMessages,
    ...actions,

    isStreaming: chat.isStreaming,
    streamError: chat.error,
  };
};
